'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCountAnimationOptions {
  end: number;
  duration?: number;
  delay?: number;
  startOnMount?: boolean;
}

interface UseCountAnimationReturn {
  ref: React.RefObject<HTMLDivElement | null>;
  count: number;
  isAnimating: boolean;
  reset: () => void;
}

export const useCountAnimation = ({
  end,
  duration = 2000,
  delay = 0,
  startOnMount = false,
}: UseCountAnimationOptions): UseCountAnimationReturn => {
  const [count, setCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const delayTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fix hydration issue
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const startAnimation = useCallback(() => {
    if (isAnimating) return;

    setIsAnimating(true);
    setCount(0);

    delayTimerRef.current = setTimeout(() => {
      const steps = Math.min(end, 100); // Max 100 steps for smooth animation
      const stepValue = end / steps;
      const stepDuration = duration / steps;

      let currentStep = 0;

      timerRef.current = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.floor(stepValue * currentStep), end);
        setCount(newValue);

        if (currentStep >= steps) {
          setCount(end);
          setIsAnimating(false);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }, stepDuration);
    }, delay);
  }, [delay, duration, end, isAnimating]);

  // Intersection Observer for triggering animation
  useEffect(() => {
    if (!isMounted || startOnMount) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted && end > 0) {
          setHasStarted(true);
          startAnimation();
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px',
      },
    );

    const node = ref.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [isMounted, startOnMount, hasStarted, end, startAnimation]);

  // Start animation on mount if specified
  useEffect(() => {
    if (startOnMount && isMounted && !hasStarted && end > 0) {
      setHasStarted(true);
      startAnimation();
    }
  }, [startOnMount, isMounted, hasStarted, end, startAnimation]);

  const reset = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    setCount(0);
    setIsAnimating(false);
    setHasStarted(false);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, []);

  return {
    ref,
    count,
    isAnimating,
    reset,
  };
};
